import { Box, Theme, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { membershipTermsPath, searchPath } from "@outschool/routes";
import { AuthTrigger, useLoginFlowContext } from "@outschool/ui-auth";
import { useSubscriptionPlans } from "@outschool/ui-components-website";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { MembershipOptionCard } from "../subscriptions/MembershipOptionCard";

export const MembershipPlan = React.forwardRef(
  (props: {}, ref: React.Ref<any>) => {
    const { t } = useTranslation("client\\components\\NewUserHome");
    const { enterLoginFlow } = useLoginFlowContext();
    const { subscriptionPlans } = useSubscriptionPlans();
    const selectedPlan = subscriptionPlans[0];
    const Link = useLinkComponent();

    return (
      <Box
        ref={ref}
        sx={(theme: Theme) => ({
          backgroundColor: "primary.100",
          paddingY: "6em",
          [theme.breakpoints.down("md")]: {
            paddingY: "4em"
          }
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1em",
            maxWidth: "548px",
            marginX: "auto",
            paddingBottom: "3em",
            textAlign: "center"
          }}
        >
          <Typography variant="display2">
            <Trans t={t}>Choose a plan that works for your family</Trans>
          </Typography>
          <Typography
            variant="body1"
            sx={(theme: Theme) => ({
              [theme.breakpoints.down("md")]: {
                paddingX: "1em"
              }
            })}
          >
            {t(
              "Whether you’re homeschooling, in a public school, or using government funds to support your kid’s learning, Outschool has a plan for you."
            )}
          </Typography>
        </Box>
        <Box
          sx={(theme: Theme) => ({
            display: "flex",
            justifyContent: "center",
            marginBottom: "3rem",
            [theme.breakpoints.down("sm")]: {
              paddingX: "1em"
            }
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              backgroundColor: "warning.100",
              borderRadius: "1rem",
              display: "flex",
              flexDirection: "row",
              paddingY: "1rem",
              paddingX: "1.6rem",
              alignItems: "center",
              [theme.breakpoints.down("md")]: {
                flexDirection: "column",
                justifyContent: "flex-start",
                gap: "1rem"
              },
              [theme.breakpoints.up("md")]: {
                width: "100%"
              },
              [theme.breakpoints.up("lg")]: {
                width: "980px"
              }
            })}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%"
              }}
            >
              <Typography
                variant="display3"
                sx={{
                  marginRight: "2rem"
                }}
              >
                <Trans t={t}>$30 OFF!</Trans>
              </Typography>
              <Typography
                variant="body1"
                sx={(theme: Theme) => ({
                  [theme.breakpoints.down("md")]: {
                    display: "none"
                  },
                  [theme.breakpoints.up("md")]: {
                    display: "block"
                  }
                })}
              >
                <Trans t={t}>
                  Get $30 off your first month and 14-day risk free
                  cancellation*
                </Trans>
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  flexGrow: 3
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    padding: "0.5rem",
                    paddingX: "1rem",
                    backgroundColor: "common.white",
                    borderRadius: "1rem"
                  }}
                >
                  {/* eslint-disable i18next/no-literal-string */}
                  CODE: BEYOND30
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                variant="body1"
                sx={(theme: Theme) => ({
                  [theme.breakpoints.down("md")]: {
                    display: "block"
                  },
                  [theme.breakpoints.up("md")]: {
                    display: "none"
                  }
                })}
              >
                <Trans t={t}>
                  Get $30 off your first month* and 14-day risk free
                  cancellation
                </Trans>
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={(theme: Theme) => ({
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "stretch",
            gap: "1em",
            position: "relative",
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
              alignItems: "center",
              paddingX: "1em"
            }
          })}
        >
          {subscriptionPlans.slice(0, 2).map((plan, index) => (
            <Box key={index} sx={{ display: "flex" }}>
              <MembershipOptionCard
                plan={plan}
                isActive={plan.uid === selectedPlan.uid}
                isRecommended={plan.uid === selectedPlan.uid}
                sx={{
                  minHeight: "none"
                }}
                priceLineContainerSx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "baseline"
                }}
                onSelect={() => {
                  const newUrl = searchPath({
                    // eslint-disable-next-line i18next/no-literal-string
                    joinMembershipModal: "true",
                    plan: plan.uid
                  });
                  enterLoginFlow({
                    authTrigger: AuthTrigger.BUY_SUBSCRIPTION,
                    shouldNavigateOverwrite: false,
                    newUrl
                  });
                }}
              />
            </Box>
          ))}
        </Box>
        <Box
          sx={{ textAlign: "center", marginTop: "3rem", color: "neutral.600" }}
        >
          <Typography variant="body2">
            <Trans t={t}>
              *Offer valid for first-time buyers only. Subject to{" "}
            </Trans>
            <Link to={membershipTermsPath()}>
              <Trans t={t}>Outschool Membership Terms</Trans>
            </Link>
            .
          </Typography>
        </Box>
      </Box>
    );
  }
);
